import {MNullable, toErrorMessage} from '../Toolkit/Utils';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import WithRouterProps from '../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../Toolkit/Components/Shell/Page';
import MComponent from '../Toolkit/Components/Component';
import EnumContext from '../Context/enum-context';
import React, {createRef, ReactElement, RefObject} from 'react';
import MAlertDialog from '../Toolkit/Components/Controls/AlertDialog';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../Api/SafeDigAccessTokenProvider';
import * as Yup from 'yup';
import {FieldProps, FormikHelpers} from 'formik';
import MForm, {MFormOutcome} from '../Toolkit/Components/Controls/Form';
import {MCloseIconName, MNavigateToIconName, MSaveIconName, MSuccessIcon} from '../Toolkit/Icons';
import MButton from '../Toolkit/Components/Controls/Button';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import withRouter from '../Toolkit/WithRouter';
import AlertDialog from '../Toolkit/Components/Controls/AlertDialog';
import FormSection from '../Toolkit/Components/Controls/FormSection';
import MFormFieldControl from '../Toolkit/Components/Controls/FormFieldControl';
import {Contact, ContactReply, CreateContactValidator} from '../Api/ContactTypes';
import {ContactClient, ContactCreateDto} from '../Api/ContactClients';
import MTextBox from '../Toolkit/Components/Controls/TextBox';

class NewContact {
    public contactType?: number = undefined;
    public contactName?: string = undefined;
    public contactEmail?: string = undefined;
}

type RouteParams = {
};

type Props = WithMsalProps & WithRouterProps<RouteParams> & {};

type State = {
    page: MPageProps;
    pageTitle: string;
    pageSubTitle: string;
    form: {
        id: string;
        isSubmitting: boolean;
        contact: NewContact;
        validationSchema: any;
    }
}

class CreateContactPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private m_newContact: MNullable<Contact> | undefined;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_dialogRef: RefObject<MAlertDialog>;
    private readonly m_contactClient: NSwagAdapter<ContactClient>

    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_pageRef = createRef<MPage>();
        this.m_dialogRef = createRef<MAlertDialog>();
        this.m_contactClient = new NSwagAdapter<ContactClient>(new ContactClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_SAFEDIGS_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));

        this.initializeState();
    }

    private bindMethods(): void {
        this.close_Click = this.close_Click.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.isFormDisabled = this.isFormDisabled.bind(this);
        this.showRegistrationCreatedDialog = this.showRegistrationCreatedDialog.bind(this);
    }

    private initializeState(): void {
        const page: MPageProps = {locked: false};

        const data: NewContact = {
            contactType: undefined,
            contactName: undefined,
            contactEmail: undefined
        };

        const validationSchema: any = Yup.object().shape({
            //contactType: CreateContactValidator.contactType,
            contactName: CreateContactValidator.contactName,
            contactEmail: CreateContactValidator.contactEmail
        });

        this.state = {
            page: page,
            pageTitle: 'Create New Contact',
            pageSubTitle: 'Create a new contact entry',
            form: {
                isSubmitting: false,
                id: 'createContactForm',
                contact: data,
                validationSchema: validationSchema
            }
        };
    }

    private isFormDisabled(): boolean {
        return this.state.form.isSubmitting;
    }

    private async close_Click(): Promise<void> {
        this.props.history.goBack();
    }

    private async page_onLoadData(): Promise<void> {

    }

    private form_onBeforeSubmit(fieldValues: NewContact, formikHelpers: FormikHelpers<NewContact>): boolean {
        this.safeSetState(draft => draft.form.isSubmitting = true);
        return false;
    }

    private async form_onSubmit(fieldValues: NewContact, formikHelpers: FormikHelpers<NewContact>): Promise<void> {
        const newContact = new ContactCreateDto({
            contactType: 0,
            contactName: fieldValues.contactName,
            contactEmail: fieldValues.contactEmail
        });

        const result = await this.m_contactClient.callWithReply(ContactReply, (client => client.create(newContact)));

        result.throwIfFailed()

        const insertIndex = this.context.enums!.subResources!.length - 1;
        this.context.enums?.subResources.splice(
            insertIndex,
            0,
            {
                id: result.reply!.contact!.id,
                name: result.reply!.contact!.contactName
            });

        this.m_newContact = result.reply!.contact;
    }

    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: NewContact, formikHelpers: FormikHelpers<NewContact>, exception: any | undefined): void {
        if(outcome === 'success') {
            this.showRegistrationCreatedDialog();
            return;
        }

        this.safeSetState(draft => draft.form.isSubmitting = false);
    }
    private form_onError(exception: any): boolean
    {
        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;
    }

    private showRegistrationCreatedDialog(): void {
        this.m_dialogRef.current!.show(
            'Create Contact',
            `A new contact has been created with ID ${this.m_newContact?.id}.`,
            MSuccessIcon,
            <MButton label="Return to contacts"
                     color="success"
                     icon={MNavigateToIconName}
                     disabled={false}
                     onClick={() => this.props.history.goBack()}/>);
    }

    private getToolbarTemplate(): ReactElement<MToolbar> {
        return (
            <MToolbar>
                <MButton label="Save"
                         formId={this.state.form.id}
                         type="submit"
                         color="success"
                         icon={MSaveIconName}
                         iconOnly={false}
                         isBusy={this.isFormDisabled}
                         disabled={this.isFormDisabled}/>
                <MSpacer/>
                <MButton label="Close"
                         icon={MCloseIconName}
                         disabled={this.isFormDisabled}
                         onClick={this.close_Click}/>
            </MToolbar>
        );

    }

    public render(): ReactElement {

        return (
            <MPage title={this.state.pageTitle}
                   subtitle={this.state.pageSubTitle}
                   locked={this.state.page.locked}
                   width={8}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <AlertDialog ref={this.m_dialogRef}/>

                <MForm formId={this.state.form.id}
                       initialValues={this.state.form.contact}
                       validationSchema={this.state.form.validationSchema}
                       onBeforeSubmit={this.form_onBeforeSubmit}
                       onSubmit={this.form_onSubmit}
                       onAfterSubmit={this.form_onAfterSubmit}
                       onError={this.form_onError}>

                    <div className="formgrid grid">
                        <div className="col-12">

                            <FormSection title="General">
                                <div className="col-12 md:col-5">
                                    <MFormFieldControl label={'Contact Name'}
                                                       fieldName={'contactName'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          disabled={false}
                                                          fieldProps={fieldProps}
                                                          maxLength={Contact.ContactNameMaxLength}
                                                          className={fieldClasses}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-8">
                                    <MFormFieldControl label="Contact Email"
                                                       fieldName="contactEmail">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          disabled={this.isFormDisabled}
                                                          fieldProps={fieldProps}
                                                          maxLength={Contact.ContactEmailMaxLength}
                                                          className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>
                            </FormSection>
                        </div>
                    </div>
                </MForm>
            </MPage>
        );
    }
}

export default withMsal(withRouter(CreateContactPage));